import { makeStyles, Theme } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import clsx from 'clsx'
import React from 'react'
import { pxToRem } from '../Utilities/unitUtils'

interface HoverTooltipStyleProps {
    backgroundColor?: string
    border?: string
    color?: string
    fontSize?: number
}

const useStyles = makeStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: (props: HoverTooltipStyleProps) =>
            props.backgroundColor ? props.backgroundColor : '#fff',
        border: (props: HoverTooltipStyleProps) =>
            props.border
                ? props.border
                : `1px solid ${theme.palette.primary.main}`,
        maxWidth: 250,
        color: (props: HoverTooltipStyleProps) =>
            props.color ? props.color : '#333333',
        fontSize: (props: HoverTooltipStyleProps) =>
            props.fontSize ? pxToRem(props.fontSize) : pxToRem(11),
        padding: 8,
        boxShadow: `2px 2px 4px 1px rgba(0, 0, 0, .4);`,
        top: 1,

        '& .MuiTooltip-arrow': {
            color: (props: HoverTooltipStyleProps) =>
                props.backgroundColor ? props.backgroundColor : '#f6f6f6',
            '&::after': {
                content: '""',
                display: 'block',
                color: '#000',
                position: 'absolute',
                width: 0,
                height: 0,
                right: '100%',
                zIndex: -1,
                border: '6px solid transparent',
            },
        },

        '&.MuiTooltip-tooltipPlacementRight': {
            left: -4.5,
            '& .MuiTooltip-arrow': {
                left: 1,
                '&::after': {
                    borderRightColor: (props: HoverTooltipStyleProps) =>
                        props.border
                            ? props.border
                            : theme.palette.primary.main,
                    left: -6.5,
                    top: 0,
                },
            },
        },
        '&.MuiTooltip-tooltipPlacementLeft': {
            left: 6,
            '& .MuiTooltip-arrow': {
                '&::after': {
                    borderLeftColor: (props: HoverTooltipStyleProps) =>
                        props.border
                            ? props.border
                            : theme.palette.primary.main,
                    left: 1,
                    top: 0,
                },
            },
        },
        '&.MuiTooltip-tooltipPlacementTop': {
            top: 6,
            '& .MuiTooltip-arrow': {
                '&::after': {
                    borderTopColor: (props: HoverTooltipStyleProps) =>
                        props.border
                            ? props.border
                            : theme.palette.primary.main,
                    top: 1,
                    left: 0,
                },
            },
        },
        '&.MuiTooltip-tooltipPlacementBottom': {
            top: -8,
            '& .MuiTooltip-arrow': {
                '&::after': {
                    borderBottomColor: (props: HoverTooltipStyleProps) =>
                        props.border
                            ? props.border
                            : theme.palette.primary.main,
                    top: -7,
                    left: 0,
                },
            },
        },
    },
}))

export type TooltipPlacements = 'top' | 'right' | 'left' | 'bottom'

export interface HoverTooltipProps extends HoverTooltipStyleProps {
    text?: NonNullable<React.ReactNode>
    children: React.ReactElement
    className?: string
    placements?: TooltipPlacements[]
    enterDelay?: number
    leaveDelay?: number
    enterNextDelay?: number
}

const HoverTooltip: React.FC<HoverTooltipProps> = ({
    text = '',
    placements = ['top', 'bottom'],
    children,
    className,
    backgroundColor,
    border,
    color,
    fontSize,
    ...props
}) => {
    const styles = useStyles({ backgroundColor, border, color, fontSize })

    return (
        <Tooltip
            {...props}
            title={text}
            className={clsx(className && className, 'hoverTooltip')}
            arrow
            interactive
            TransitionProps={{
                timeout: 0,
            }}
            PopperProps={{
                modifiers: {
                    flip: {
                        behavior: placements,
                    },
                },
            }}
            placement={placements[0]}
            classes={{
                tooltipArrow: styles.tooltip,
            }}
        >
            {children}
        </Tooltip>
    )
}

export default HoverTooltip
